import { Platform, Prisma, TaskType } from "@prisma/client";
import { APIGuild } from "discord-api-types/v10";
export * from "./TriggerCreate";
export * from "./getAvailableVariables";

export function types(): string {
  return "types";
}

export type DiscordTaskEvent = TaskParams & {
  communityId: string;
  workspace: string;
  user?: {
    id: string;
  };
  interaction?: DiscordInteraction;
};

export type DiscordInteraction = {
  id: string;
  token: string;
  channelId: string; //channel id where the interaction was triggered.
  modalData?: any;
};

const triggerWithStepsTransitions = Prisma.validator<Prisma.TriggerArgs>()({
  include: {
    steps: {
      include: {
        transitions: true,
      },
    },
  },
});

export type TriggerWithStepsTransitions = Prisma.TriggerGetPayload<
  typeof triggerWithStepsTransitions
>;

export type User = {
  id: string;
  username: string;
  discriminator?: string;
};

export type OnTriggerParams = {
  user?: User; // we don't always have user here, e.g. "time" trigger
  where: Partial<Prisma.TriggerWhereInput>;
  interaction?: InteractionParams;
};

export type OnTriggerWebhookParams = {
  where: Partial<Prisma.TriggerWhereInput>;
};

type UserResponseMeta = {
  userId: string;
  channelId: string;
  roleIds?: string[];
};

type UserMessage = UserResponseMeta & {
  type: "message";
  messageId: string;
  content: string;
};

type UserReaction = UserResponseMeta & {
  type: "reaction";
  messageId: string;
  content: string;
};

type UserReactionRemove = UserResponseMeta & {
  type: "reaction-remove";
  messageId: string;
  content: string;
};

type UserButton = UserResponseMeta & {
  type: "button";
  messageId: string;
  buttonId: string;
  label?: string;
  emoji?: string;
  content: string; // label ?? '', for ease of use in templates: {{step1.response.content}}
};

type UserModalSubmit = UserResponseMeta & {
  type: "modal-submit";
  messageId: string;
  modalData: Record<string, any>;
  modalId: string;
};
type UserMultipleSelect = UserResponseMeta & {
  type: "multiple-select";
  messageId: string;
  optionIds: string[];
  content?: string[];
};

type UserCaptchaAnswer = UserResponseMeta & {
  type: "captcha-result";
  userId: string;
  succeeded: boolean;
  modalId?: string;
};

export type UserResponse =
  | UserMessage
  | UserReaction
  | UserButton
  | UserReactionRemove
  | UserMultipleSelect
  | UserCaptchaAnswer
  | UserModalSubmit;

export type OnUserResponseParams = {
  platform: Platform;
  communityId: string;
  interaction?: InteractionParams;
} & UserResponse;

/**
 * `TaskExecutor` is an abstraction that supports different ways of
 * running a task. For example, there are two other executors:
 * `LambdaExecutor` and `SQSExecutor`. The `DiscordExecutor` is the
 * simplest way that supports running tasks in a long-running process.
 * There’s also a `DummyExecutor` in `onTrigger.spec.ts` which does
 * nothing just for the testing process.
 *
 * This TaskExecutor abstraction is probably not perfect at this point,
 * since I extracted the code from the previous lambda design and massage
 * it into the current architecture. I think we can evolve it when
 * adding more use cases. For example, when we want to mix tasks from
 * different platforms, we probably need to augment it somehow.
 */
export interface TaskExecutor {
  getTargetPlatforms: () => Platform[];
  runTask: (
    taskId: string,
    platform: Platform,
    taskType: TaskType,
    params: object
  ) => Promise<any>;
  validateParams: (
    platform: Platform,
    taskType: TaskType,
    params: object
  ) => void;
}

export type TaskParams = object;

export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

export type DirectMessageResult = {
  type: "success";
  channelId: string;
  messageId: string;
  userId: string;
};

export type ClientRequestResult = DirectMessageResult;

export type HttpRequestResult =
  | {
      type: "json";
      status: number;
      content: object;
      httpResponse: { type: "json"; status: number; content: object };
    }
  | {
      type: "text";
      status: number;
      content: string;
      httpResponse: { type: "text"; status: number; content: string };
    }
  | {
      type: "error";
      status?: number;
      content: string;
      httpResponse: { type: "error"; status?: number; content: string };
    };

export const USER_RESPONSE_TYPES = [
  "message",
  "reaction",
  "reaction-remove",
  "button",
  "multiple-select",
  "captcha-result",
  "modal-submit",
];

export function isUserResponse(data: any): data is UserResponse {
  return USER_RESPONSE_TYPES.includes(data?.type);
}

export function isHttpRequestResult(data: any): data is HttpRequestResult {
  return ["json", "text", "error"].includes(data?.type);
}

export function isClientResult(data: any): data is ClientRequestResult {
  return ["success", "fail"].includes(data?.type);
}

// https://stackoverflow.com/a/52221718/3973320
export type AllUnionMemberKeys<T> = T extends any ? keyof T : never;

export function isEmailVerifiedWebhookPayload(
  data: any
): data is EmailVerifiedWebhookPayload {
  return (
    data?.type === "webhook:email-verified" &&
    ["taskId", "email"].every((k) => data[k])
  );
}

type WebhookPayloadCommon = { taskId: string };

export type EmailVerifiedWebhookPayload = WebhookPayloadCommon & {
  type: "webhook:email-verified";
  email: string;
};

export type WebhookPayload = EmailVerifiedWebhookPayload;

export type WhenMatchableData = WebhookPayload | OnUserResponseParams;

export type GlobalVars = {
  user?: User; // we don't always have user here, e.g. "time" trigger
  platform: Platform;
  communityId: string;
  interaction?: InteractionParams;
  command?: {
    options?: { [key: string]: any };
  };
};

export type WebhookTriggerGlobalVars = {
  platform: Platform;
  request: { [key: string]: any };
};

export type InteractionParams = {
  id: string;
  token: string;
  channelId: string;
  userId: string;
};

export type WebhookTriggerRequest =
  | {
      type: string;
      status: string;
      message: string;
      headers?: {
        [key: string]: string[];
      };
    }
  | {
      type: string;
      meta: { [key: string]: string };
      headers: {
        [key: string]: string[];
      };
      query: string;
      body: {
        [key: string]: string[];
      };
      method: HttpMethod;
    };

export type OauthDiscordAuthorization = {
  accessToken: string;
  tokenType: string;
  refreshToken: string;
  scope: string;
  userId: string; // supabase user id from Auth.users table
  id?: string; // public.user_discord_tokens pk
};
/**
 * With IndexedObject, we can get its property value using a string varable key,
 * https://www.typescriptlang.org/docs/handbook/2/objects.html#index-signatures
 *
 * For example:
 *
 *   const o: IndexedObject = {}
 *   o[key] = 123
 */
export type IndexedObject = { [k: string]: any };

/**
 * Discord teams where Dots has been added to their server (supabase model)
 */

export type DiscordGuilds = {
  /** Primary key. Index: discord_teams_pkey */
  discord_id: string;

  name?: string | null;
  /** Default value: now() */
  joined_ts?: Date | null;
  onboarding_category?: string | null;
  /** A second onboarding category so we can flex up to 100 channels */
  onboarding_category_2?: string | null;
  /** Index: idx_discord_guilds_workspace */
  workspace?: string | null;
  /**
   * Boolean indicating if this guild is active or not. If false, don't run ETL for this guild
   * Default value: true
   */
  is_active?: boolean | null;
  /** Last time batch ETL was ran for this guild */
  last_synced_at?: Date | null;
  /** Discord admin channel id */
  dots_admins_channel?: string;
};

export type DiscordDotGuilds = APIGuild & {
  has_dots_bot: boolean;
  can_user_add_bot: boolean;
};

export type discord_usersId = string & { " __flavor"?: "discord_users" };

/**
 * Discord users of Dots that went through OAuth (supabase model)
 */
export type DiscordUsers = {
  /** Primary key. Index: discord_users_pkey */
  discord_id: discord_usersId;
  /** Index: idx_discord_users_guild */
  guild?: string | null;
  username?: string | null;
  discriminator?: string | null;
  avatar?: string | null;
  auth_users_uid?: string | null;
  approved?: boolean | null;
  email?: string | null;
  timezone?: string | null;
  discord_user_id?: string | null;
  /** Index: idx_discord_users_workspace */
  workspace?: string | null;
  /** Default value: now() */
  created_at?: Date | null;
};

/**
 * Many to many table between Auth.users and public.workspaces
 * It indicates which workspaces a user is a member of
 */
export type UsersWorkspaces = {
  id: string;
  auth_users_uuid?: string | null;
  workspace?: string | null;
  created_at?: Date | null;
  updated_at?: Date | null;
};

/**
 * Many to many table between Auth.users and public.workspaces
 * It indicates which workspaces a user is a member of
 */
export type Workspace = {
  id?: string; // id is marked optional here so we can create without id, and let supabase auto-generate id
  name: string;
  svix_app_id?: string | null;
  paid_user: boolean;
};

export type SvixIntegrations = {
  id: string;
  app_id: string;
  workspace: string;
  key: string;
};

//Type to be used in DiscordTextEditor
export type PersonalizationValue = {
  id: string; // id, it will be added to html element as data-id attribute
  value: string; // the value to be inserted into the editor when option is clicked
  userText?: string; // a text to be displayed in the editor when a filter is activated, if it's not provided, value property swill be used instead
};

/**
 * Type for discord_active_members RPC
 */
export type ActiveMembersRpc = {
  username: string;
  avatar: string;
  parent_count: number;
  reaction_count: number;
  thread_count: number;
};

/**
 * Type for any RPC function that returns a total_count, like discord_active_members_count
 */
export type CountRpc = {
  total_count: number;
};

/**
 * Type for discord_guild_message_count RPC
 */
export type GuildMessageCountRpc = {
  messages: number;
};

/**
 * Type for discord_most_active_channels RPC
 */
export type MostActiveChannelsRpc = {
  channel_id: string;
  channel_name: string;
  messages: number;
};

/**
 * Type for discord_new_member_count RPC
 */
export type NewMemberRpc = {
  total_count: number;
  non_participating: number;
};

/**
 * Type for discord_popular_threads RPC, used by discord_popular_threads
 */
export type PopularThreadsRpc = {
  id: string;
  name: string;
  channel: string;
  channel_id: string;
  member_count: number;
  thread_count: number;
  created_at: string;
};

/**
 * Type for discord_avg_daily_msgs_one_month RPC
 */
export type AvgDailyMessages = {
  daily_avg: number;
};

/**
 * Type for mesages_reaction_count_in_a_range RPC
 */
export type MessagesWithReactionCount = {
  message_id: string;
  reactions_count: number;
  content: string;
};

/**
 * Type for BotTemplates table
 */
export type BotTemplates = {
  id: number;
  name: string;
  description: string;
  details_html: string;
  gif_url: string;
  trigger: string;
  slug: string;
  tags: string;
  emoji: string;
};
